import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CssBaseline,
  FormControlLabel,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";

import { createPricingHolidayUseCase } from "../domain/usecases/createPricingHolidayUseCase";
import Topbar from "../../../components/topbar/TopBar";
import { getCountryListUseCase } from "../../geography_catalog/country/domain/usecases/getCountryListUseCase";
import { getDepartmentListByCountryIdUseCase } from "../../geography_catalog/department/domain/usecases/getDepartmentListByCountryIdUseCase";
import { getProvinceListByDepartmentIdUseCase } from "../../geography_catalog/province/domain/usecases/getProvinceListByDepartmentIdUseCase";

function formatDateForInput(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatDateForPayload(value) {
  if (!value) {
    return "";
  }

  const [year = "", month = "", day = ""] = value.split("-");
  return `${day}-${month}-${year}`;
}

function createInitialForm() {
  return {
    name: "",
    holidayDate: formatDateForInput(new Date()),
    countryId: "",
    departmentId: "",
    provinceId: "",
    multiplier: "1.0",
    active: true,
  };
}

function isSuccessResponse(response) {
  const message = response?.message?.toLowerCase() || "";
  return (
    response?.success === true ||
    message.includes("success") ||
    message.includes("created")
  );
}

export default function PricingHolidayCreatePage() {
  const navigate = useNavigate();
  const [form, setForm] = useState(createInitialForm);
  const [countries, setCountries] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({
    open: false,
    type: "success",
    text: "",
  });

  useEffect(() => {
    const loadCountries = async () => {
      try {
        const records = await getCountryListUseCase();
        setCountries(records);
        if (records.length > 0) {
          setForm((prev) => ({
            ...prev,
            countryId: String(records[0].id),
          }));
        }
      } catch (error) {
        setMessage({
          open: true,
          type: "error",
          text: error.message || "No se pudo cargar la lista de paises.",
        });
      }
    };

    loadCountries();
  }, []);

  useEffect(() => {
    const loadDepartments = async () => {
      if (!form.countryId) {
        setDepartments([]);
        setProvinces([]);
        return;
      }

      try {
        const records = await getDepartmentListByCountryIdUseCase(
          form.countryId,
        );
        setDepartments(records);
        setForm((prev) => ({
          ...prev,
          departmentId: "",
          provinceId: "",
        }));
      } catch (error) {
        setDepartments([]);
        setProvinces([]);
        setMessage({
          open: true,
          type: "error",
          text: error.message || "No se pudo cargar la lista de departamentos.",
        });
      }
    };

    loadDepartments();
  }, [form.countryId]);

  useEffect(() => {
    const loadProvinces = async () => {
      if (!form.departmentId) {
        setProvinces([]);
        return;
      }

      try {
        const records = await getProvinceListByDepartmentIdUseCase(
          form.departmentId,
        );
        setProvinces(records);
        setForm((prev) => ({
          ...prev,
          provinceId: "",
        }));
      } catch (error) {
        setProvinces([]);
        setMessage({
          open: true,
          type: "error",
          text: error.message || "No se pudo cargar la lista de provincias.",
        });
      }
    };

    loadProvinces();
  }, [form.departmentId]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckChange = (event) => {
    setForm((prev) => ({
      ...prev,
      active: event.target.checked,
    }));
  };

  const handleSave = async () => {
    const payload = {
      name: form.name.trim(),
      holidayDate: formatDateForPayload(form.holidayDate),
      countryId: Number(form.countryId),
      departmentId: form.departmentId ? Number(form.departmentId) : null,
      provinceId: form.provinceId ? Number(form.provinceId) : null,
      multiplier: Number(form.multiplier),
      active: Boolean(form.active),
    };

    try {
      setSaving(true);
      const response = await createPricingHolidayUseCase(payload);

      if (!isSuccessResponse(response)) {
        throw new Error("No se pudo crear la fecha festiva.");
      }

      setMessage({
        open: true,
        type: "success",
        text: "Fecha festiva creada correctamente.",
      });

      setForm({
        ...createInitialForm(),
        countryId: countries[0] ? String(countries[0].id) : "",
      });
      setDepartments([]);
      setProvinces([]);
    } catch (error) {
      setMessage({
        open: true,
        type: "error",
        text: error.message || "No se pudo crear la fecha festiva.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <CssBaseline />
      <main className="container">
        <Topbar />
        <Box
          sx={{
            minHeight: "100vh",
            color: "#000",
            "& .MuiTypography-root": { color: "#000" },
            p: 1,
          }}
        >
          <Stack spacing={3} alignItems="center">
            <Paper
              elevation={0}
              sx={{
                width: "100%",
                maxWidth: 760,
                borderRadius: 3,
                overflow: "hidden",
                border: "1px solid rgba(16, 24, 40, 0.08)",
              }}
            >
              <Box
                sx={{
                  px: { xs: 2, md: 3 },
                  py: { xs: 2.5, md: 3 },
                  background:
                    "linear-gradient(135deg, #0f3d3e 0%, #165c53 55%, #2d8b74 100%)",
                }}
              >
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", md: "center" }}
                  spacing={2}
                >
                  <Box>
                    <Typography
                      variant="h4"
                      fontWeight={800}
                      sx={{ color: "#fff !important" }}
                    >
                      Crear Fechas Festivas
                    </Typography>
                    <Typography
                      sx={{ mt: 0.75, color: "#fff !important", opacity: 0.88 }}
                    >
                      Registra Un Feriado y su multiplicador de Precio.
                    </Typography>
                  </Box>

                  <Button
                    variant="contained"
                    onClick={() => navigate("/admin/precio-feriado/lista")}
                    sx={{
                      px: 2.25,
                      py: 1,
                      color: "#fff",
                      bgcolor: "rgba(255,255,255,0.16)",
                      borderRadius: 2,
                      textTransform: "none",
                      boxShadow: "none",
                      "&:hover": {
                        bgcolor: "rgba(255,255,255,0.24)",
                        boxShadow: "none",
                      },
                    }}
                  >
                    Ver Lista
                  </Button>
                </Stack>
              </Box>

              <Box sx={{ p: { xs: 2, md: 4 } }}>
                <Paper
                  elevation={0}
                  sx={{
                    mx: "auto",
                    maxWidth: 480,
                    borderRadius: 3,
                    border: "1px solid rgba(20, 83, 45, 0.12)",
                    p: { xs: 1, md: 2 },
                  }}
                >
                  <Stack spacing={2}>
                    <TextField
                      fullWidth
                      label="Nombre"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                    />
                    <TextField
                      fullWidth
                      type="date"
                      label="Fecha festiva"
                      name="holidayDate"
                      value={form.holidayDate}
                      onChange={handleChange}
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      fullWidth
                      select
                      label="Pais"
                      name="countryId"
                      value={form.countryId}
                      onChange={handleChange}
                      sx={{
                        "& .MuiInputBase-input": { color: "#fff" },
                        "& .MuiSelect-select": { color: "#fff" },
                        "& .MuiInputLabel-root": { color: "#fff" },
                        "& .MuiInputLabel-root.Mui-focused": { color: "#fff" },
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "rgba(255,255,255,0.35)",
                        },
                        "& .MuiSelect-icon": { color: "#fff" },
                      }}
                    >
                      {countries.map((country) => (
                        <MenuItem key={country.id} value={String(country.id)}>
                          {country.name} - {country.code}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      fullWidth
                      select
                      label="Departamento"
                      name="departmentId"
                      value={form.departmentId}
                      onChange={handleChange}
                      sx={{
                        "& .MuiInputBase-input": { color: "#fff" },
                        "& .MuiSelect-select": { color: "#fff" },
                        "& .MuiInputLabel-root": { color: "#fff" },
                        "& .MuiInputLabel-root.Mui-focused": { color: "#fff" },
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "rgba(255,255,255,0.35)",
                        },
                        "& .MuiSelect-icon": { color: "#fff" },
                      }}
                    >
                      <MenuItem value="">
                        <em>Sin departamento</em>
                      </MenuItem>
                      {departments.map((department) => (
                        <MenuItem
                          key={department.id}
                          value={String(department.id)}
                        >
                          {department.name} - {department.code}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      fullWidth
                      select
                      label="Provincia"
                      name="provinceId"
                      value={form.provinceId}
                      onChange={handleChange}
                      sx={{
                        "& .MuiInputBase-input": { color: "#fff" },
                        "& .MuiSelect-select": { color: "#fff" },
                        "& .MuiInputLabel-root": { color: "#fff" },
                        "& .MuiInputLabel-root.Mui-focused": { color: "#fff" },
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "rgba(255,255,255,0.35)",
                        },
                        "& .MuiSelect-icon": { color: "#fff" },
                      }}
                    >
                      <MenuItem value="">
                        <em>Sin provincia</em>
                      </MenuItem>
                      {provinces.map((province) => (
                        <MenuItem key={province.id} value={String(province.id)}>
                          {province.name} - {province.code}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      fullWidth
                      type="number"
                      label="Multiplicador"
                      name="multiplier"
                      value={form.multiplier}
                      onChange={handleChange}
                      inputProps={{ step: "0.1", min: "1" }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={form.active}
                          onChange={handleCheckChange}
                          sx={{
                            color: "#165c53",
                            "&.Mui-checked": {
                              color: "#165c53",
                            },
                          }}
                        />
                      }
                      label="Activo"
                      sx={{
                        mx: 0,
                        "& .MuiFormControlLabel-label": {
                          color: "#000",
                          fontWeight: 600,
                        },
                      }}
                    />

                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={handleSave}
                      disabled={saving}
                      sx={{
                        mt: 1,
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 700,
                        bgcolor: "#165c53",
                        color: "#fff",
                        "&:hover": {
                          bgcolor: "#12483f",
                        },
                      }}
                    >
                      {saving ? "Guardando..." : "Guardar"}
                    </Button>
                  </Stack>
                </Paper>
              </Box>
            </Paper>
          </Stack>

          <Snackbar
            open={message.open}
            autoHideDuration={4000}
            onClose={() =>
              setMessage((prev) => ({
                ...prev,
                open: false,
              }))
            }
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            <Alert
              severity={message.type}
              variant="filled"
              onClose={() =>
                setMessage((prev) => ({
                  ...prev,
                  open: false,
                }))
              }
            >
              {message.text}
            </Alert>
          </Snackbar>
        </Box>
      </main>
    </>
  );
}
